import { useEffect } from 'react';
import { Result, Button, Typography, Spin } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetSubscriptionStatusQuery } from '../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../services/usersApi';
import { useAppDispatch } from '../hooks/redux';
import { updateSubscriptionInfo } from '../store/slices/authSlice';
import { ONBOARDING_PAGE_BACKGROUND } from '../styles/onboardingSurface';
import { clearPlanIntent } from '../utils/planIntent';

const { Text } = Typography;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const { data: subscriptionStatus, isLoading, refetch } = useGetSubscriptionStatusQuery();
  const { data: limitInfo, refetch: refetchLimitInfo } = useGetUserLimitInfoQuery();

  // Ya contrató: el plan que eligió en la landing deja de importar. Stripe regresa aquí
  // con una redirección de página completa, así que el onComplete del checkout nunca
  // corre y limpiar solo ahí no bastaba.
  useEffect(() => {
    clearPlanIntent();
  }, []);

  useEffect(() => {
    // Refetch subscription status after returning from Stripe
    if (sessionId) {
      const timer = setTimeout(() => {
        refetch();
        refetchLimitInfo();
      }, 2000); // Give Stripe webhook time to process

      return () => clearTimeout(timer);
    }
  }, [sessionId, refetch, refetchLimitInfo]);

  // Update Redux state when subscription info is loaded
  useEffect(() => {
    if (subscriptionStatus && limitInfo) {
      // Determine if subscription is active (not expired)
      const isActive = subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing';

      dispatch(updateSubscriptionInfo({
        plan: subscriptionStatus.planName || limitInfo.plan,
        isTrialExpired: !isActive && limitInfo.isTrialExpired,
        maxUsers: limitInfo.maxUsers,
        trialEndDate: subscriptionStatus.trialEnd || limitInfo.trialEndDate,
      }));

    }
  }, [subscriptionStatus, limitInfo, dispatch]);

  const handleGoToDashboard = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: ONBOARDING_PAGE_BACKGROUND
      }}>
        <Spin size="large" tip="Verificando tu suscripción..." />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: ONBOARDING_PAGE_BACKGROUND,
      padding: 20,
    }}>
      <Result
        icon={<CheckCircleOutlined style={{ color: 'var(--color-primary)' }} />}
        status="success"
        title="¡Suscripción Exitosa!"
        subTitle={
          <div>
            <Text>
              Tu suscripción al plan <strong>{subscriptionStatus?.planName || 'seleccionado'}</strong> ha sido activada correctamente.
            </Text>
            <br />
            <Text type="secondary">
              {subscriptionStatus?.status === 'trialing'
                ? 'Tu periodo de prueba de 7 días ha comenzado. No se te cobrará hasta que termine.'
                : 'Ya puedes disfrutar de todas las funcionalidades de tu plan.'}
            </Text>
          </div>
        }
        extra={[
          <Button
            type="primary"
            key="dashboard"
            size="large"
            onClick={handleGoToDashboard}
            style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
          >
            Ir al Dashboard
          </Button>,
        ]}
        style={{
          background: 'var(--bg-primary)',
          padding: '48px 32px',
          borderRadius: 16,
          boxShadow: '0 4px 24px var(--shadow-color)',
        }}
      />
    </div>
  );
};

export default SubscriptionSuccess;
